import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MapPin, House, CheckCircle, Circle, CircleDashed,
  ImagesSquare, PaperPlaneRight, ChatCircle, Sparkle, Warning,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const PHONE_KEY_PREFIX = "tenant_portal_phone_";

const STATUS_META = {
  pool:      { label: "Submitted",   color: "bg-slate-600" },
  assigned:  { label: "Assigned",    color: "bg-cyan-700" },
  in_progress: { label: "In Progress", color: "bg-amber-500" },
  completed: { label: "Completed",   color: "bg-emerald-600" },
  cancelled: { label: "Cancelled",   color: "bg-slate-400" },
  canceled:  { label: "Cancelled",   color: "bg-slate-400" },
};

/**
 * TenantHistoryView — Roadmap #7 tenant self-service portal.
 * Renders behind the existing RequestRadarPortal gate when info.portal_enabled=true.
 * Phase A: history + status + timeline + add-photos.
 * Phase B (if info.chat_enabled): message thread with crew.
 * Phase C (if info.schedule_enabled): accept/decline proposed visit windows.
 */
export default function TenantHistoryView({ token, info, onBack }) {
  const [phone, setPhone] = useState(() => localStorage.getItem(PHONE_KEY_PREFIX + token) || "");
  const [committed, setCommitted] = useState(!!localStorage.getItem(PHONE_KEY_PREFIX + token));
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [contact, setContact] = useState({ name: "", phone: "" });
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState("");

  const load = async (usePhone) => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${BASE}/api/public/report/${token}/my-requests?phone=${encodeURIComponent(usePhone)}`);
      setRequests(r.data.requests || []);
      setContact(r.data.contact || { name: "", phone: "" });
      setCommitted(true);
      localStorage.setItem(PHONE_KEY_PREFIX + token, usePhone);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load your requests");
      setCommitted(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (phone && !committed) load(phone); /* eslint-disable-next-line */ }, []);

  if (openId) {
    return <RequestDetail token={token} jobId={openId} phone={phone} info={info}
      onBack={() => setOpenId(null)} onRefresh={() => load(phone)}/>;
  }

  // Phone gate
  if (!committed) {
    return (
      <div className="min-h-screen bg-slate-100" data-testid="tenant-history-gate">
        <Header info={info} title="My Requests" onBack={onBack}/>
        <div className="max-w-lg mx-auto p-4">
          <div className="bg-white border-2 border-[#0f172a] p-5">
            <div className="font-display font-black text-xl mb-2">Look up your requests</div>
            <p className="text-sm text-slate-600 mb-3">
              Enter the phone number you used when reporting. We'll match your prior requests instantly — no password needed.
            </p>
            <input type="tel" inputMode="numeric" placeholder="(555) 123-4567"
              value={phone} onChange={e => setPhone(e.target.value)}
              data-testid="tenant-history-phone-input"
              className="w-full h-12 px-3 border-2 border-[#0f172a] text-lg font-mono"/>
            <button
              onClick={() => phone.trim() && load(phone.trim())}
              disabled={!phone.trim() || loading}
              data-testid="tenant-history-phone-submit"
              className="mt-3 w-full h-12 bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a] disabled:opacity-50">
              {loading ? "Looking up…" : "Find My Requests"}
            </button>
            {err && <div className="mt-3 text-sm text-red-700" data-testid="tenant-history-err">{err}</div>}
          </div>
        </div>
      </div>
    );
  }

  // History list
  return (
    <div className="min-h-screen bg-slate-100" data-testid="tenant-history-list">
      <Header info={info} title="My Requests" onBack={onBack} subtitle={contact.name ? `Welcome back, ${contact.name}` : ""}/>
      <div className="max-w-lg mx-auto p-3 space-y-2 pb-24">
        {loading && <div className="text-center font-black uppercase text-xs tracking-widest text-slate-500 py-8">Loading…</div>}
        {!loading && requests.length === 0 && (
          <div className="bg-white border-2 border-slate-300 p-6 text-center">
            <div className="font-display font-black text-lg mb-1">No requests yet</div>
            <div className="text-xs text-slate-500 mb-3">Once you file a request, it'll show up here for status + updates.</div>
            <button onClick={onBack} className="h-10 px-4 bg-cyan-500 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a]">
              File a New Request
            </button>
          </div>
        )}
        {!loading && requests.map(r => {
          const meta = STATUS_META[r.status] || STATUS_META.pool;
          return (
            <button key={r.id} onClick={() => setOpenId(r.id)}
              data-testid={`tenant-req-${r.id}`}
              className="w-full text-left p-3 border-2 border-slate-300 hover:border-[#0f172a] bg-white transition-colors">
              <div className="flex items-center gap-2">
                <div className="font-display font-black text-base leading-tight flex-1 min-w-0 truncate">{r.title}</div>
                <div className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white ${meta.color}`}>
                  {meta.label}
                </div>
              </div>
              <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5 flex-wrap">
                <span>#{r.ref}</span>
                {r.category && <><span>·</span><span>{r.category}</span></>}
                {r.created_at && <><span>·</span><span>{new Date(r.created_at).toLocaleDateString()}</span></>}
              </div>
              {r.assigned_to_name && r.status !== "completed" && (
                <div className="mt-2 text-xs text-slate-700 flex items-center gap-1">
                  <Sparkle size={12} className="text-cyan-600"/> Assigned to <b>{r.assigned_to_name}</b>
                </div>
              )}
              {r.scheduled_window && r.schedule_status === "proposed" && (
                <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
                  ⏰ Confirm visit window — tap to review
                </div>
              )}
              {r.thread_count > 0 && (
                <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-1">
                  <ChatCircle size={11}/> {r.thread_count} message{r.thread_count === 1 ? "" : "s"}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Header({ info, title, subtitle = "", onBack }) {
  return (
    <header className="bg-[#0f172a] text-white" style={info?.brand_color ? { background: info.brand_color } : {}}>
      <div className="max-w-lg mx-auto px-3 py-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="tenant-history-back"
          className="h-9 w-9 bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center">
          <ArrowLeft size={16}/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] truncate">{info?.crew_name} · RequestRadar™</div>
          <div className="font-display font-black text-lg leading-tight">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-300 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="max-w-lg mx-auto px-3 pb-3 text-xs inline-flex items-center gap-1">
        <House size={12}/> {info?.property_address}
      </div>
    </header>
  );
}

function RequestDetail({ token, jobId, phone, info, onBack, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [schedBusy, setSchedBusy] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/api/public/report/${token}/request/${jobId}?phone=${encodeURIComponent(phone)}`);
      setDetail(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Could not load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const sendMsg = async () => {
    if (!msg.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${BASE}/api/public/report/${token}/request/${jobId}/comment?phone=${encodeURIComponent(phone)}`,
        { text: msg.trim() });
      setMsg("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed")); }
    finally { setPosting(false); }
  };

  const uploadPhoto = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(
          `${BASE}/api/public/report/${token}/request/${jobId}/photo?phone=${encodeURIComponent(phone)}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Photo added — the office will see it");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
    finally { setUploading(false); }
  };

  const respondSchedule = async (action, note = "") => {
    setSchedBusy(true);
    try {
      await axios.post(`${BASE}/api/public/report/${token}/request/${jobId}/schedule-confirm?phone=${encodeURIComponent(phone)}`,
        { action, note });
      toast.success(action === "accept" ? "Confirmed" : action === "decline" ? "Declined" : "Reschedule requested");
      await load();
      onRefresh?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setSchedBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-100">
      <Header info={info} title="Request" onBack={onBack}/>
      <div className="p-4 text-red-700 text-sm">{err}</div>
    </div>
  );
  if (!detail) return (
    <div className="min-h-screen bg-slate-100">
      <Header info={info} title="Loading…" onBack={onBack}/>
    </div>
  );

  const status = detail.status || "pool";
  const meta = STATUS_META[status] || STATUS_META.pool;
  return (
    <div className="min-h-screen bg-slate-100" data-testid="tenant-request-detail">
      <Header info={info} title={"#" + detail.ref} subtitle={detail.title} onBack={onBack}/>
      <div className="max-w-lg mx-auto p-3 space-y-3 pb-32">
        <div className="bg-white border-2 border-[#0f172a] p-4">
          <div className="flex items-center gap-2">
            <div className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white ${meta.color}`}
              data-testid={`tenant-detail-status-${status}`}>
              {meta.label}
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {detail.category} · {detail.unit ? `Unit ${detail.unit}` : "Property"}
            </div>
          </div>
          <div className="mt-2 font-display font-black text-xl leading-tight">{detail.title}</div>
          {detail.description && (
            <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</div>
          )}
        </div>

        {/* Schedule confirmation (Phase C) */}
        {detail.scheduled_window && detail.schedule_enabled && (
          <div className={`bg-white border-2 p-4 ${detail.schedule_status === "confirmed" ? "border-emerald-500" : detail.schedule_status === "proposed" ? "border-amber-500" : "border-slate-300"}`}
            data-testid="tenant-schedule-block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Proposed Visit Window</div>
            <div className="font-display font-black text-base">
              {fmtWindow(detail.scheduled_window)}
            </div>
            {detail.schedule_status === "proposed" && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                <button onClick={() => respondSchedule("accept")} disabled={schedBusy}
                  data-testid="tenant-schedule-accept"
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] disabled:opacity-50">Accept</button>
                <button onClick={() => respondSchedule("reschedule")} disabled={schedBusy}
                  data-testid="tenant-schedule-reschedule"
                  className="h-11 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] disabled:opacity-50">Reschedule</button>
                <button onClick={() => respondSchedule("decline")} disabled={schedBusy}
                  data-testid="tenant-schedule-decline"
                  className="h-11 bg-slate-600 hover:bg-slate-700 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] disabled:opacity-50">Decline</button>
              </div>
            )}
            {detail.schedule_status === "confirmed" && (
              <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                <CheckCircle weight="fill" size={12}/> You confirmed this window
              </div>
            )}
            {detail.schedule_status === "declined" && (
              <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-600">
                You declined — the office will propose a new window
              </div>
            )}
            {detail.schedule_status === "reschedule_requested" && (
              <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
                Reschedule requested — office will follow up
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white border-2 border-[#0f172a] p-4" data-testid="tenant-timeline">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Timeline</div>
          <TL icon={CheckCircle} done label="Submitted" sub={detail.created_at && new Date(detail.created_at).toLocaleString()}/>
          <TL icon={CheckCircle} done={!!detail.assigned_to_name}
             label={detail.assigned_to_name ? `Assigned to ${detail.assigned_to_name}` : "Assigned"}
             sub={detail.assigned_at && new Date(detail.assigned_at).toLocaleString()}/>
          <TL icon={detail.en_route_at ? CheckCircle : CircleDashed}
             done={!!detail.en_route_at}
             active={status === "in_progress"}
             label="On the way / working"
             sub={detail.en_route_at && new Date(detail.en_route_at).toLocaleString()}/>
          <TL icon={status === "completed" ? CheckCircle : CircleDashed}
             done={status === "completed"}
             label="Completed"
             sub={status === "completed" ? (detail.completion_notes || "Marked complete") : ""} last/>
        </div>

        {/* Add photos */}
        {status !== "completed" && (
          <div className="bg-white border-2 border-[#0f172a] p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Add more photos</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => cameraRef.current?.click()} disabled={uploading}
                data-testid="tenant-add-photo-camera"
                className="h-11 bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] flex items-center justify-center gap-1 disabled:opacity-50">
                📷 Take Photo
              </button>
              <button onClick={() => galleryRef.current?.click()} disabled={uploading}
                data-testid="tenant-add-photo-gallery"
                className="h-11 bg-white text-[#0f172a] font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] flex items-center justify-center gap-1 disabled:opacity-50">
                <ImagesSquare size={13}/> From Photos
              </button>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                onChange={e => e.target.files && uploadPhoto(Array.from(e.target.files))}
                className="hidden"/>
              <input ref={galleryRef} type="file" accept="image/*" multiple
                onChange={e => e.target.files && uploadPhoto(Array.from(e.target.files))}
                className="hidden"/>
            </div>
            {uploading && <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Uploading…</div>}
          </div>
        )}

        {/* Chat thread (Phase B) */}
        {detail.chat_enabled && (
          <div className="bg-white border-2 border-[#0f172a] p-4" data-testid="tenant-chat">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <ChatCircle size={12}/> Message the office
            </div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {(detail.tenant_thread || []).length === 0 && (
                <div className="text-[11px] text-slate-400 italic py-2">No messages yet.</div>
              )}
              {(detail.tenant_thread || []).map((m, i) => {
                const isTenant = m.from === "tenant";
                return (
                  <div key={i} data-testid={`tenant-chat-msg-${i}`}
                    className={`p-2 max-w-[85%] ${isTenant ? "ml-auto bg-cyan-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                    <div className="text-[9px] font-black uppercase tracking-widest opacity-70">
                      {m.author || (isTenant ? "You" : "Office")} · {m.at && new Date(m.at).toLocaleString()}
                    </div>
                    <div className="text-sm mt-0.5 whitespace-pre-wrap">{m.text}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex gap-1">
              <input value={msg} onChange={e => setMsg(e.target.value)}
                placeholder="Type a message…" data-testid="tenant-chat-input"
                className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm"/>
              <button onClick={sendMsg} disabled={posting || !msg.trim()}
                data-testid="tenant-chat-send"
                className="h-11 px-4 bg-cyan-500 text-white font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] disabled:opacity-50">
                <PaperPlaneRight weight="fill" size={13}/>
              </button>
            </div>
          </div>
        )}

        {status === "completed" && detail.completion_notes && (
          <div className="bg-emerald-50 border-2 border-emerald-500 p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-1">Resolution</div>
            <div className="text-sm text-emerald-900 whitespace-pre-wrap">{detail.completion_notes}</div>
          </div>
        )}

        <div className="pt-4 text-[11px] text-slate-500 flex items-center gap-1 justify-center">
          <Phone size={11}/> Emergency? Call {info?.crew_phone || "the office"}
        </div>
      </div>
    </div>
  );
}

function TL({ icon: Icon, label, sub, done = false, active = false, last = false }) {
  const color = done ? "text-emerald-600" : active ? "text-amber-600" : "text-slate-300";
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <Icon weight={done || active ? "fill" : "regular"} size={18} className={color}/>
        {!last && <div className={`w-0.5 flex-1 ${done ? "bg-emerald-600" : "bg-slate-200"} min-h-[20px]`}/>}
      </div>
      <div className="pb-4 flex-1 min-w-0">
        <div className={`text-sm font-black ${done ? "text-slate-900" : active ? "text-amber-800" : "text-slate-400"}`}>{label}</div>
        {sub && <div className={`text-[10px] mt-0.5 ${done ? "text-slate-500" : active ? "text-amber-700" : "text-slate-400"}`}>{sub}</div>}
      </div>
    </div>
  );
}

function fmtWindow(w) {
  if (!w) return "";
  try {
    const s = new Date(w.start), e = new Date(w.end);
    const day = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const st = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const et = e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${day} · ${st} – ${et}`;
  } catch { return w.start; }
}
